import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Input, DatePicker, Space, Image as AntdImage, ConfigProvider } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { searchAttendance, exportAttendance } from "../../utils/api/attendance";
import CustomToast from "../../components/CustomToast";
import 'antd/dist/reset.css';
import dayjs from 'dayjs';
import moment from 'moment';

function AttendanceManage() {
  const [details, setMessage] = useState({ message: "", type: "" });
  const [showToast, setShowToast] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  const [searchData, setSearchData] = useState({
    agent: "",
    dateTo: dayjs(),
    dateFrom: dayjs(),
  });
  
  const [modalVisible, setModalVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imageTitle, setImageTitle] = useState("");
  
  // // Base URL for API
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const columns = [
    {
      title: <span className="text-white px-2 py-1 block">NAME</span>,
      dataIndex: 'agent',
      key: 'agent',
      width: 200,
      align: 'center',
    },
    {
      title: <span className="text-white py-1 block">USERNAME</span>,
      dataIndex: 'mobile_number',
      key: 'username',
      width: 150,
      align: 'center',
    },
    {
      title: <span className="text-white py-1 block">TIME IN</span>,
      dataIndex: 'time_in',
      key: 'time_in',
      width: 200,
      align: 'center',
      render: (text) => text ? moment(text).format("MMMM Do YYYY, h:mm:ss A") : 'N/A',
    },
    {
      title: <span className="text-white py-1 block">TIME OUT</span>,
      dataIndex: 'time_out',
      key: 'time_out',
      width: 200,
      align: 'center',
      render: (text) => text ? moment(text).format("MMMM Do YYYY, h:mm:ss A") : 'N/A',
    },
    {
      title: <span className="text-white py-1 block">TARGET QUOTA</span>,
      dataIndex: 'target_quota',
      key: 'target_quota',
      width: 150,
      align: 'center',
      render: (text) => text || '0',
    },
    {
      title: <span className="text-white py-1 block">REACHED QUOTA</span>,
      dataIndex: 'reached_quota',
      key: 'reached_quota',
      width: 150,
      align: 'center',
    },
    {
      title: <span className="text-white py-1 block">PHOTO</span>,
      dataIndex: 'photo',
      key: 'photo',
      width: 150,
      align: 'center',
      render: (text) => text ? (
        <Button 
          type="default" 
          onClick={() => {
            // Check if the URL is already complete (starts with http:// or https://)
            const isCompleteUrl = /^https?:\/\//i.test(text);
            let fullUrl = text;
            
            if (!isCompleteUrl) {
              // Only prepend base URL if it's a relative path
              const cleanPath = text.replace(/^\/+/, '');
              fullUrl = `${API_BASE_URL.replace(/\/+$/, '')}/${cleanPath}`;
            }
            
            setImageUrl(fullUrl);
            setImageTitle('Attendance Photo');
            setModalVisible(true);
          }}
        >
          View Photo
        </Button>
      ) : '-',
    },
  ];

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

    const handleExport = async () => {
        try {
          setLoading(true);
          // Create a fresh data object with current state values
          const exportData = {
            dateFrom: searchData.dateFrom,
            dateTo: searchData.dateTo,
            agent: searchData.agent
          };
          console.log('Current searchData before export:', searchData);
          console.log('ExportData being sent:', exportData);
          await exportAttendance(exportData);
          setMessage({
            message: 'Export started successfully',
            type: 'success'
          });
          setShowToast(true);
          // Auto-hide the toast after 3 seconds
          setTimeout(() => {
            setShowToast(false);
          }, 3000);
        } catch (error) {
          console.error('Export failed:', error);
          setMessage({
            message: 'Export failed. Please try again.',
            type: 'error'
          });
          setShowToast(true);
          // Auto-hide the error toast after 5 seconds
          setTimeout(() => {
            setShowToast(false);
          }, 5000);
        } finally {
          setLoading(false);
        }
      };

  const handleSearch = async (pagination = { current: 1, pageSize: 10 }) => {
    try {
      setLoading(true);
      const response = await searchAttendance(
        searchData.dateFrom.format('YYYY-MM-DD'),
        searchData.dateTo.format('YYYY-MM-DD'),
        searchData.agent
      );

      if (response?.data?.data) {
        const formattedData = response.data.data.map(item => ({
          ...item,
          key: item.id || Math.random().toString(36).substr(2, 9),
        }));
        
        setData(formattedData);
        setPagination({
          ...pagination,
          total: response.data.total || formattedData.length,
        });
      } else {
        setData([]);
        const responseMessage = response?.data?.messages?.error;
        setMessage({
          message: responseMessage ?? "No data found",
          type: "warning",
        });
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error);
      const responseMessage = error.response?.data?.messages?.error || 'Something went wrong.';
      setMessage({
        message: responseMessage,
        type: "danger",
      });
      setShowToast(true);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    handleSearch(pagination);
  };

  useEffect(() => {
    handleSearch(pagination);
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#FB9C0C',
          borderRadius: 6,
        },
      }}
    >
    <div className="p-2">
      <div className="text-center">
        <div className="col-12">
          <h1 className="title">ATTENDANCES</h1>
        </div>
      </div>
      
      <div className="d-flex flex-wrap gap-3 mb-4 align-items-end">
        <div style={{ width: "250px" }}>
          <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Date From</label>
          <DatePicker
            style={{ width: '100%' }}
            value={searchData.dateFrom}
            onChange={(date) => setSearchData({ ...searchData, dateFrom: date })}
            format="YYYY-MM-DD"
            allowClear={false}
          />
        </div>
        
        <div style={{ width: "250px" }}>
          <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Date To</label>
          <DatePicker
            style={{ width: '100%' }}
            value={searchData.dateTo}
            onChange={(date) => setSearchData({ ...searchData, dateTo: date })}
            format="YYYY-MM-DD"
            allowClear={false}
          />
        </div>
        
        <div style={{ width: "250px" }}>
          <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Agent</label>
          <Input
            placeholder="Search Agent"
            value={searchData.agent}
            onChange={(e) => setSearchData({ ...searchData, agent: e.target.value })}
          />
        </div>
        
        <div className="d-flex align-items-end">
          <Space>
            <Button type="primary" onClick={() => handleSearch(pagination)}>
              Search
            </Button>
            <Button type="primary" onClick={handleExport} disabled={!data || data.length === 0} loading={loading}>
              Export
            </Button>
          </Space>
        </div>
      </div>
      
      <div className="row">
        <div className="col-12">
          <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
            <Table
              // className="no-hover"
              columns={columns}
              dataSource={data}
              rowKey="id"
              pagination={{
                ...pagination,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total) => `Total ${total} items`,
                onChange: (page, pageSize) => handleSearch(page, pageSize),
              }}
              scroll={{ x: 'max-content' }}
              bordered
              size="small"
              loading={loading}
            />
          </div>
        </div>
      </div>
      
      <CustomToast
        type={details.type}
        message={details.message}
        title="Attendance"
        show={showToast}
        setShow={setShowToast}
      />
      
      <Modal
        title="Attendance Photo"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        centered
        width={800}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '400px',
          backgroundColor: '#f0f2f5',
          padding: '20px'
        }}>
          {imageUrl ? (
            <div style={{ maxWidth: '100%', maxHeight: '70vh', overflow: 'auto' }}>
              <img 
                src={imageUrl} 
                alt="Attendance"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '70vh',
                  display: 'block',
                  margin: '0 auto'
                }}
                onError={(e) => {
                  console.error('Error loading image:', imageUrl);
                  e.target.onerror = null;
                  e.target.src = 'data:image/png;base64,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'; 
                }}
              />
              <div style={{ textAlign: 'center', marginTop: '10px', color: '#666' }}>
                {imageUrl}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', marginBottom: '10px', color: '#666' }}>
                No image available
              </div>
              <div style={{ fontSize: '14px', color: '#999' }}>
                The requested image could not be loaded.
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
    </ConfigProvider>
  );
}

export default AttendanceManage;
